import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { ApplicationDenyPayload } from 'commons/types/DTO/bank'
import { Request } from 'commons/utils/request'
import { FF, TextareaInput, Toast, handleFormSubmissionError } from 'ui/components'
import { Button } from 'ui/components/Buttons'
import { Dialog, DialogFace } from 'ui/components/Dialog'

import { s } from './styles'

type Props = {
  onClose: () => void
  applicationId: number
  companyIndex: number
}

export const ApproveCompanyDialog = (props: Props) => {
  const { onClose, applicationId, companyIndex } = props
  const router = useRouter()

  const { mutateAsync, isPending } = useMutation<void, Error, ApplicationDenyPayload>({
    mutationFn: (props) => Request.put(Request.urls.bank.applicationDeny, props),
    onSuccess: () => {
      onClose()
      router.invalidate()
      Toast.success('Company approved')
    },
    onError: async (error) => {
      handleFormSubmissionError(error, formApi)
    }
  })

  const formApi = useForm({
    defaultValues: { denialReason: '' },
    onSubmit: ({ value: { denialReason } }) =>
      mutateAsync({ denialReason, applicationId, isDenied: false, companyIndex })
  })

  return (
    <Dialog
      id={`ApproveCompanyDialog-${companyIndex}`}
      onClose={() => {
        formApi.reset()
        onClose()
      }}
      face={DialogFace.PRIMARY}
      title="Approve Company"
      isSubmitting={isPending}
      customFooterActions={
        <>
          <Button key="save" face="primary" onClick={formApi.handleSubmit} loading={isPending} data-qa="dialog-save">
            Approve
          </Button>
          <Button
            key="cancel"
            face="secondary"
            onClick={() => {
              formApi.reset()
              onClose()
            }}
            data-qa="dialog-cancel"
            disabled={isPending}
          >
            Cancel
          </Button>
        </>
      }
    >
      <s.Box>
        <p>Are you sure you want to approve this company?</p>
        <formApi.Field
          name="denialReason"
          children={(field) => (
            <FF
              label="Note (optional)"
              field={field}
              input={(p) => (
                <TextareaInput hint="The notes will be saved in Internal Notes (only visible to Bank) " {...p} />
              )}
            />
          )}
        />
      </s.Box>
    </Dialog>
  )
}
